import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import { Tag, DollarSign, FileText, Calendar, CheckCircle, XCircle, Edit, Trash2, Plus, ArrowLeft } from 'lucide-react';

const ServiceManagement = ({ onBack }) => {
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingService, setEditingService] = useState(null);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const data = await apiService.getAdminServices();
      setServices(data);
    } catch (error) {
      console.error("Failed to fetch services", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveService = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());
    data.base_price = parseFloat(data.base_price);
    
    // Convert empty strings to null, otherwise parse float
    data.price_single = data.price_single ? parseFloat(data.price_single) : null;
    data.price_quarterly = data.price_quarterly ? parseFloat(data.price_quarterly) : null;
    data.price_half_yearly = data.price_half_yearly ? parseFloat(data.price_half_yearly) : null;
    data.price_yearly = data.price_yearly ? parseFloat(data.price_yearly) : null;

    data.is_active = data.is_active === 'on';

    try {
      if (editingService?.id) {
        await apiService.updateAdminService(editingService.id, data);
      } else {
        await apiService.createAdminService(data);
      }
      setEditingService(null);
      
      // Refresh data
      const updatedServices = await apiService.getAdminServices();
      setServices(updatedServices);
      
      if (selectedService && editingService?.id === selectedService.id) {
         const updated = updatedServices.find(s => s.id === selectedService.id);
         setSelectedService(updated);
      }
    } catch (error) {
      console.error("Failed to save service", error);
    }
  };

  const handleDeleteService = async (id) => {
    if (!window.confirm('确定要删除吗？')) return;
    try {
      await apiService.deleteAdminService(id);
      if (selectedService?.id === id) {
        setSelectedService(null);
      }
      fetchServices();
    } catch (error) {
      console.error("Failed to delete service", error);
    }
  };

  return (
    <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-md overflow-hidden min-h-[500px]">
      <div className="p-4 border-b flex items-center justify-between bg-gray-50">
        <div className="flex items-center">
          <button 
            onClick={onBack} 
            className="mr-4 text-gray-600 hover:text-indigo-600 flex items-center transition-colors"
          >
            <ArrowLeft className="h-5 w-5 mr-1" />
            仪表盘
          </button>
          <h2 className="text-xl font-semibold text-gray-800">服务管理</h2>
        </div>
        {!selectedService && (
          <button
            onClick={() => setEditingService({})}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 text-sm flex items-center shadow-sm transition-colors"
          >
            <Plus className="h-4 w-4 mr-1" />
            新增服务
          </button>
        )}
      </div>

      <div className="p-6">
        {loading && <div className="text-center py-10 text-gray-500">加载中...</div>}

        {!loading && !selectedService && (
          <div className="overflow-hidden rounded-lg border border-gray-200 shadow-sm">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">名称</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">代码</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">价格</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">状态</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {services.map((s, index) => (
                  <tr key={s.id} className={`hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{s.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs font-mono">{s.code}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">¥{s.base_price}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {s.is_active ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle size={12} className="mr-1" /> 上架
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          <XCircle size={12} className="mr-1" /> 下架
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button 
                        onClick={() => setSelectedService(s)} 
                        className="text-indigo-600 hover:text-indigo-900 mr-4 inline-flex items-center transition-colors"
                      >
                        查看
                      </button>
                      <button 
                        onClick={(e) => { e.stopPropagation(); handleDeleteService(s.id); }} 
                        className="text-red-600 hover:text-red-900 inline-flex items-center transition-colors"
                      >
                        <Trash2 size={14} className="mr-1" /> 删除
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && selectedService && (
          <div className="animate-fade-in">
            <button 
              onClick={() => setSelectedService(null)} 
              className="mb-6 text-indigo-600 hover:text-indigo-800 flex items-center font-medium transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              服务列表
            </button>
            
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <div className="flex items-center">
                  <div className="p-2 bg-indigo-100 rounded-lg mr-3">
                    <Tag className="text-indigo-600 h-6 w-6" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">{selectedService.name}</h2>
                    <p className="text-sm text-gray-500 font-mono">{selectedService.code}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setEditingService(selectedService)}
                  className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center shadow-sm transition-colors"
                >
                  <Edit size={16} className="mr-2" />
                  编辑服务
                </button>
              </div>

              <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2 flex items-center">
                      <DollarSign size={16} className="mr-1" /> 价格策略
                    </h3>
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">基础价格:</span>
                        <span className="font-medium">¥{selectedService.base_price}</span>
                      </div>
                      {selectedService.price_single !== null && selectedService.price_single !== undefined && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">单次购买:</span>
                          <span className="font-medium">¥{selectedService.price_single}</span>
                        </div>
                      )}
                      {selectedService.price_quarterly !== null && selectedService.price_quarterly !== undefined && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">季度订阅:</span>
                          <span className="font-medium">¥{selectedService.price_quarterly}</span>
                        </div>
                      )}
                      {selectedService.price_half_yearly !== null && selectedService.price_half_yearly !== undefined && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">半年订阅:</span>
                          <span className="font-medium">¥{selectedService.price_half_yearly}</span>
                        </div>
                      )}
                      {selectedService.price_yearly !== null && selectedService.price_yearly !== undefined && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">年度订阅:</span>
                          <span className="font-medium">¥{selectedService.price_yearly}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2 flex items-center">
                      <CheckCircle size={16} className="mr-1" /> 状态
                    </h3>
                    <div>
                      {selectedService.is_active ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-200">
                          <span className="h-2 w-2 rounded-full bg-green-500 mr-2"></span>
                          正在上架销售中
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800 border border-red-200">
                          <span className="h-2 w-2 rounded-full bg-red-500 mr-2"></span>
                          已下架停止销售
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2 flex items-center">
                      <FileText size={16} className="mr-1" /> 服务描述
                    </h3>
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 min-h-[100px]">
                      <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                        {selectedService.description || '暂无描述信息'}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1 flex items-center">
                        <Calendar size={14} className="mr-1" /> 创建时间
                      </h3>
                      <p className="text-sm text-gray-900 font-medium">{selectedService.created_at?.split('T')[0] || '-'}</p>
                    </div>
                    <div>
                      <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1 flex items-center">
                        <Calendar size={14} className="mr-1" /> 最后更新
                      </h3>
                      <p className="text-sm text-gray-900 font-medium">{selectedService.updated_at?.split('T')[0] || '-'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Service Edit Modal */}
        {editingService && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all scale-100">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                <h3 className="text-lg font-bold text-gray-900">
                  {editingService.id ? '编辑服务' : '新增服务'}
                </h3>
                <button onClick={() => setEditingService(null)} className="text-gray-400 hover:text-gray-600">
                  <XCircle size={24} />
                </button>
              </div>
              
              <form onSubmit={handleSaveService} className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">服务名称</label>
                    <input 
                      name="name" 
                      defaultValue={editingService.name} 
                      required 
                      className="block w-full border border-gray-300 rounded-lg shadow-sm p-2.5 focus:ring-indigo-500 focus:border-indigo-500 transition-colors" 
                      placeholder="例如：八字排盘专业版"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">服务代码 (Code)</label>
                    <input 
                      name="code" 
                      defaultValue={editingService.code} 
                      required 
                      className="block w-full border border-gray-300 rounded-lg shadow-sm p-2.5 focus:ring-indigo-500 focus:border-indigo-500 transition-colors font-mono text-sm" 
                      placeholder="例如：bazi_pro"
                    />
                    <p className="mt-1 text-xs text-gray-500">唯一标识符，用于系统内部识别</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">基础价格 (元)</label>
                    <div className="relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">¥</span>
                      </div>
                      <input 
                        name="base_price" 
                        type="number" 
                        step="0.01" 
                        defaultValue={editingService.base_price} 
                        required 
                        className="block w-full pl-7 border border-gray-300 rounded-lg shadow-sm p-2.5 focus:ring-indigo-500 focus:border-indigo-500 transition-colors" 
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">单次/月付</label>
                      <input name="price_single" type="number" step="0.01" defaultValue={editingService.price_single} className="block w-full border border-gray-300 rounded-lg p-2.5" placeholder="可选" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">季度订阅</label>
                      <input name="price_quarterly" type="number" step="0.01" defaultValue={editingService.price_quarterly} className="block w-full border border-gray-300 rounded-lg p-2.5" placeholder="可选" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">半年订阅</label>
                      <input name="price_half_yearly" type="number" step="0.01" defaultValue={editingService.price_half_yearly} className="block w-full border border-gray-300 rounded-lg p-2.5" placeholder="可选" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">年度订阅</label>
                      <input name="price_yearly" type="number" step="0.01" defaultValue={editingService.price_yearly} className="block w-full border border-gray-300 rounded-lg p-2.5" placeholder="可选" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">描述</label>
                    <textarea 
                      name="description" 
                      defaultValue={editingService.description} 
                      rows={3}
                      className="block w-full border border-gray-300 rounded-lg shadow-sm p-2.5 focus:ring-indigo-500 focus:border-indigo-500 transition-colors" 
                      placeholder="简要描述该服务的功能和特点..."
                    />
                  </div>
                  
                  <div className="flex items-center p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <input 
                      id="is_active"
                      name="is_active" 
                      type="checkbox" 
                      defaultChecked={editingService.is_active !== false} 
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer" 
                    />
                    <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900 cursor-pointer select-none">
                      立即上架销售
                    </label>
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button 
                    type="button" 
                    onClick={() => setEditingService(null)} 
                    className="bg-white text-gray-700 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                  >
                    取消
                  </button>
                  <button 
                    type="submit" 
                    className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 font-medium shadow-sm transition-colors"
                  >
                    保存
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceManagement;
